/**
 * Info client for read-only queries
 * Follows pattern from pkg/netrunner/clients for public/session auth operations
 * Automatically converts satoshi strings to decimals for display
 */

import { HttpClient } from "../../transport/http/HttpClient";
import { fromSatoshi } from "../../utils/math";
import { InfoClientConfig } from "../../types";
import {
  RawBucket,
  Market,
  GetMarketsResponse,
  RawMarketStats,
  ExchangeInfoResponse,
  RawOrderbook,
  OrderbookEntry,
  TradesResponse,
  RawTrade,
  RawAggTrade,
  KlinesResponse,
  RawKline,
  RawPremiumIndex,
  FundingRateResponse,
  RawFundingRate,
  OpenInterestResponse,
  TWAPResponse,
  IndexInfoResponse,
  AssetIndexResponse,
  ConstituentsResponse,
  DeliveryPriceResponse,
  BasisResponse,
  LongShortRatioResponse,
  PositionsResponse,
  RawPosition,
  RawBalance,
  GetBalancesResponse,
  OrdersResponse,
  RawOrder,
  MarketDataResponse,
  MarketCandlesResponse,
  NonceStatusResponse,
  SupportedActionsResponse,
  ValidateActionResponse,
  HealthSummaryResponse,
  ServerTimeResponse,
  TPSMetricsResponse,
  LatencyMetricsResponse,
} from "../../types/api";

export class InfoClient {
  private httpClient: HttpClient;
  private sessionToken?: string;

  constructor(config: InfoClientConfig) {
    this.httpClient = new HttpClient(config.baseURL);
    this.sessionToken = config.sessionToken;
  }

  /**
   * Set session token for authenticated requests
   */
  setSessionToken(token: string): void {
    this.sessionToken = token;
  }

  /**
   * Get headers for requests
   * Follows pattern from DexClient.Do() for public/session auth
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/json, */*",
    };

    if (this.sessionToken) {
      headers["Authorization"] = `Bearer ${this.sessionToken}`;
    }

    return headers;
  }

  // ============================================================================
  // MARKET DATA ENDPOINTS (Public)
  // ============================================================================

  /**
   * Get all markets. Uses /market/v1/all (market_api.md). Response may have count or total; SDK normalizes to total.
   */
  async getMarkets(): Promise<GetMarketsResponse> {
    const response = await this.httpClient.get(
      "/market/v1/all",
      this.getHeaders(),
    );
    const body = response.body as GetMarketsResponse;
    if (body && typeof body === "object" && "markets" in body) {
      const total = body.count ?? body.total ?? (body.markets?.length ?? 0);
      return { markets: body.markets ?? [], total, count: body.count };
    }
    return { markets: [], total: 0 };
  }

  /**
   * Get markets with filters.
   * Uses HTTP endpoint /market/v1/all with query params.
   */
  async getMarketsWithFilters(
    filter?: string,
    activeOnly?: boolean,
  ): Promise<GetMarketsResponse> {
    let path = "/market/v1/all";
    const params: string[] = [];
    if (filter) {
      params.push(`filter=${filter}`);
    }
    if (activeOnly) {
      params.push("active_only=true");
    }
    if (params.length > 0) {
      path += "?" + params.join("&");
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as GetMarketsResponse;
    if (body && typeof body === "object" && "markets" in body) {
      const total = body.count ?? body.total ?? (body.markets?.length ?? 0);
      return { markets: body.markets ?? [], total, count: body.count };
    }
    return { markets: [], total: 0 };
  }

  /**
   * Get market by ID (marketIndex).
   * Uses HTTP endpoint /market/v1/market/{marketId}.
   */
  async getMarketByID(marketId: string): Promise<Market> {
    const response = await this.httpClient.get(
      `/market/v1/market/${marketId}`,
      this.getHeaders(),
    );
    return response.body as Market;
  }

  /**
   * Get market by trading symbol (e.g. BTC-USDC-PERP).
   * Uses HTTP endpoint /market/v1/market/by-symbol/{symbol} per market_api.md.
   */
  async getMarketBySymbol(symbol: string): Promise<Market> {
    const response = await this.httpClient.get(
      `/market/v1/market/by-symbol/${encodeURIComponent(symbol)}`,
      this.getHeaders(),
    );
    return response.body as Market;
  }

  /**
   * Get market by ticker/symbol. Uses /market/v1/market/by-ticker/{ticker}.
   * For doc path by-symbol use getMarketBySymbol() (market_api.md documents both).
   */
  async getMarketByTicker(ticker: string): Promise<Market> {
    const response = await this.httpClient.get(
      `/market/v1/market/by-ticker/${encodeURIComponent(ticker)}`,
      this.getHeaders(),
    );
    return response.body as Market;
  }

  /**
   * Get market by name/moniker.
   * Uses HTTP endpoint /market/v1/market/by-moniker/{moniker}.
   * @deprecated Prefer getMarketByTicker(symbol) per API.
   */
  async getMarketByMoniker(moniker: string): Promise<Market> {
    const response = await this.httpClient.get(
      `/market/v1/market/by-moniker/${encodeURIComponent(moniker)}`,
      this.getHeaders(),
    );
    return response.body as Market;
  }

  /**
   * Get market statistics (ticker data)
   * Uses HTTP endpoint /market/v1/market/{marketId}/stats (matches Go client GetMarketStats)
   */
  async getMarketStats(marketId: string): Promise<RawMarketStats> {
    const response = await this.httpClient.get(
      `/market/v1/market/${marketId}/stats`,
      this.getHeaders(),
    );
    return response.body as RawMarketStats;
  }

  /**
   * Get market ticker (alias for getMarketStats)
   * @deprecated Use getMarketStats instead
   */
  async getTicker(marketId: string): Promise<RawMarketStats> {
    return this.getMarketStats(marketId);
  }

  /**
   * Get exchange info
   * Uses HTTP endpoint /market/v1/exchangeInfo (matches Go client GetExchangeInfo)
   */
  async getExchangeInfo(): Promise<ExchangeInfoResponse> {
    const response = await this.httpClient.get(
      "/market/v1/exchangeInfo",
      this.getHeaders(),
    );
    return response.body as ExchangeInfoResponse;
  }

  /**
   * Query orderbook snapshot for a market.
   * Proto: QueryOrderBookSnapshot — GET /clob/v1/orderbook/{marketIndex}
   * Response PriceLevels ({price, quantity, orderCount}) are normalized to OrderbookEntry tuples.
   * See: proto-lib/proto/clob/v1/query.proto, orchestrator E2E TestOrderbookSnapshot.
   */
  async getOrderbook(
    marketIndex: string,
    convertToDecimal: boolean = true,
    depth: number = 20,
  ): Promise<RawOrderbook> {
    const depthValue = depth > 0 && depth < 100 ? depth : 20;
    const path = `/clob/v1/orderbook/${marketIndex}?depth=${depthValue}`;

    const response = await this.httpClient.get(path, this.getHeaders());
    const raw = response.body as Record<string, unknown>;

    return {
      marketIndex: raw.marketIndex as string | undefined,
      symbol: raw.symbol as string | undefined,
      bids: this.normalizePriceLevels(raw.bids, convertToDecimal),
      asks: this.normalizePriceLevels(raw.asks, convertToDecimal),
      sequenceId: raw.sequenceId != null ? Number(raw.sequenceId) : undefined,
      checksum: raw.checksum as string | undefined,
      blockHeight: raw.blockHeight as string | undefined,
      updatedAt: raw.updatedAt as string | undefined,
    };
  }

  /**
   * Normalize PriceLevel objects from proto JSON into OrderbookEntry tuples.
   * Proto format: {price: string, quantity: string, orderCount: number}
   * Output format: [price, quantity] as OrderbookEntry
   */
  private normalizePriceLevels(
    levels: unknown,
    convertToDecimal: boolean,
  ): OrderbookEntry[] {
    if (!Array.isArray(levels)) return [];
    return levels.map((level): OrderbookEntry => {
      let price: string;
      let quantity: string;
      if (typeof level === "object" && level !== null && "price" in level) {
        // PriceLevel object from proto JSON: {price, quantity, orderCount}
        const pl = level as { price: string; quantity: string };
        price = pl.price;
        quantity = pl.quantity;
      } else if (Array.isArray(level) && level.length >= 2) {
        // Already in tuple format (e.g., from WebSocket or other sources)
        price = String(level[0]);
        quantity = String(level[1]);
      } else {
        return [0, 0];
      }
      if (convertToDecimal) {
        return [fromSatoshi(price), fromSatoshi(quantity)];
      }
      return [price, quantity];
    });
  }

  /**
   * Get market trades by market ID
   * Uses HTTP endpoint /clob/v1/market/{marketIndex}/trades (proto-aligned, E2E-verified in place_order_e2e_test.go)
   * Response format: { trades: RawTrade[], totalCount: number, marketIndex: string, symbol: string, timestamp }
   */
  async getMarketTrades(marketId: string, limit?: number): Promise<RawTrade[]> {
    let path = `/clob/v1/market/${marketId}/trades`;
    if (limit && limit > 0) {
      path += `?limit=${limit}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    console.debug(
      "[InfoClient] getMarketTrades response for marketId:",
      marketId,
      "response:",
      response.body,
    );

    const body = response.body as TradesResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "trades" in body &&
      Array.isArray(body.trades)
    ) {
      return body.trades;
    }

    // Fallback for legacy formats (direct array or { data: [...] })
    if (Array.isArray(body)) {
      return body;
    }
    // Check for legacy format with 'data' property (not part of TradesResponse type)
    const bodyAny = body as any;
    if (bodyAny?.data && Array.isArray(bodyAny.data)) {
      return bodyAny.data;
    }

    console.warn(
      "[InfoClient] getMarketTrades: Unexpected response structure:",
      response.body,
    );
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get trades by symbol
   * Uses HTTP endpoint /clob/v1/trades?symbol={symbol} (matches Go client GetTrades)
   * Response format: { trades: RawTrade[], totalCount: number, marketIndex: string, symbol: string, timestamp }
   */
  async getTrades(symbol: string, limit?: number): Promise<RawTrade[]> {
    let path = `/clob/v1/trades?symbol=${symbol}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as TradesResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "trades" in body &&
      Array.isArray(body.trades)
    ) {
      return body.trades;
    }

    // Fallback for legacy formats (direct array or { data: [...] })
    if (Array.isArray(body)) {
      return body;
    }
    // Check for legacy format with 'data' property (not part of TradesResponse type)
    const bodyAny = body as any;
    if (bodyAny?.data && Array.isArray(bodyAny.data)) {
      return bodyAny.data;
    }

    return [];
  }

  /**
   * Get aggregate trades
   * Uses HTTP endpoint /market/v1/aggTrades (matches Go client GetAggTrades)
   */
  async getAggTrades(
    symbol: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawAggTrade[]> {
    let path = `/market/v1/aggTrades?symbol=${symbol}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get klines (candlestick data)
   * Uses HTTP endpoint /clob/v1/klines (matches Go client GetKlines)
   */
  async getKlines(
    symbol: string,
    interval: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawKline[]> {
    let path = `/clob/v1/klines?symbol=${symbol}&interval=${interval}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as KlinesResponse;
    return Array.isArray(body) ? body : [];
  }

  /**
   * Get continuous klines
   * Uses HTTP endpoint /market/v1/continuousKlines (matches Go client GetContinuousKlines)
   */
  async getContinuousKlines(
    pair: string,
    contractType: string,
    interval: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawKline[]> {
    let path = `/market/v1/continuousKlines?pair=${pair}&contractType=${contractType}&interval=${interval}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get premium index (mark price and funding rate)
   * Uses HTTP endpoint /market/v1/premiumIndex (matches Go client GetPremiumIndex)
   */
  async getPremiumIndex(
    symbol: string,
  ): Promise<RawPremiumIndex | RawPremiumIndex[]> {
    let path = "/market/v1/premiumIndex";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawPremiumIndex);
  }

  /**
   * Get funding rate history
   * Uses HTTP endpoint /market/v1/fundingRate (matches Go client GetFundingRate)
   */
  async getFundingRate(
    symbol: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<RawFundingRate[]> {
    let path = "/market/v1/fundingRate";
    const params: string[] = [];
    if (symbol) {
      params.push(`symbol=${symbol}`);
    }
    if (limit && limit > 0) {
      params.push(`limit=${limit}`);
    }
    if (startTime && startTime > 0) {
      params.push(`startTime=${startTime}`);
    }
    if (endTime && endTime > 0) {
      params.push(`endTime=${endTime}`);
    }
    if (params.length > 0) {
      path += "?" + params.join("&");
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as FundingRateResponse;
    if (Array.isArray(body)) {
      return body;
    }
    if (
      body &&
      typeof body === "object" &&
      "data" in body &&
      Array.isArray(body.data)
    ) {
      return body.data;
    }
    return body ? [body as RawFundingRate] : [];
  }

  /**
   * Get funding info
   * Uses HTTP endpoint /market/v1/fundingInfo (matches Go client GetFundingInfo)
   */
  async getFundingInfo(): Promise<RawFundingRate[]> {
    const response = await this.httpClient.get(
      "/market/v1/fundingInfo",
      this.getHeaders(),
    );
    const body = response.body;
    return Array.isArray(body) ? body : [];
  }

  /**
   * Get delivery price
   * Uses HTTP endpoint /market/v1/deliveryPrice (matches Go client GetDeliveryPrice)
   */
  async getDeliveryPrice(symbol: string): Promise<DeliveryPriceResponse> {
    const response = await this.httpClient.get(
      `/market/v1/deliveryPrice?pair=${symbol}`,
      this.getHeaders(),
    );
    return response.body as DeliveryPriceResponse;
  }

  /**
   * Get open interest
   * Uses HTTP endpoint /market/v1/openInterest (matches Go client GetOpenInterest)
   */
  async getOpenInterest(symbol: string): Promise<OpenInterestResponse> {
    const response = await this.httpClient.get(
      `/market/v1/openInterest?symbol=${symbol}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get top long/short position ratio
   * Uses HTTP endpoint /market/v1/topLongShortPositionRatio (matches Go client GetTopLongShortPositionRatio)
   */
  async getTopLongShortPositionRatio(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<LongShortRatioResponse | LongShortRatioResponse[]> {
    let path = `/market/v1/topLongShortPositionRatio?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as LongShortRatioResponse);
  }

  /**
   * Get global long/short account ratio
   * Uses HTTP endpoint /market/v1/globalLongShortAccountRatio (matches Go client GetGlobalLongShortAccountRatio)
   */
  async getGlobalLongShortAccountRatio(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<LongShortRatioResponse | LongShortRatioResponse[]> {
    let path = `/market/v1/globalLongShortAccountRatio?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as LongShortRatioResponse);
  }

  /**
   * Get 24hr ticker statistics. Uses /market/v1/ticker/24hr?ticker= per market_api.
   */
  async getTicker24hr(
    ticker: string,
  ): Promise<RawMarketStats | RawMarketStats[]> {
    let path = "/market/v1/ticker/24hr";
    if (ticker) {
      path += `?ticker=${encodeURIComponent(ticker)}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawMarketStats);
  }

  /**
   * Get ticker price. Uses /market/v1/ticker/price?ticker= per market_api.
   */
  async getTickerPrice(
    ticker: string,
  ): Promise<RawMarketStats | RawMarketStats[]> {
    let path = "/market/v1/ticker/price";
    if (ticker) {
      path += `?ticker=${encodeURIComponent(ticker)}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawMarketStats);
  }

  /**
   * Get orderbook ticker (best bid/ask). Uses /market/v1/ticker/bookTicker?ticker= per market_api.
   */
  async getOrderbookTicker(
    ticker: string,
  ): Promise<RawOrderbook | RawOrderbook[]> {
    let path = "/market/v1/ticker/bookTicker";
    if (ticker) {
      path += `?ticker=${encodeURIComponent(ticker)}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as RawOrderbook);
  }

  /**
   * Get orderbook ticker by symbol.
   * @deprecated Use getOrderbookTicker(ticker) which uses /ticker/bookTicker?ticker= per API.
   */
  async getOrderbookMoniker(
    symbol: string,
  ): Promise<RawOrderbook | RawOrderbook[]> {
    return this.getOrderbookTicker(symbol);
  }

  /**
   * Get basis data
   * Uses HTTP endpoint /market/v1/basis (matches Go client GetBasis)
   */
  async getBasis(
    symbol: string,
    period: string,
    limit?: number,
    startTime?: number,
    endTime?: number,
  ): Promise<BasisResponse | BasisResponse[]> {
    let path = `/market/v1/basis?symbol=${symbol}&period=${period}`;
    if (limit && limit > 0) {
      path += `&limit=${limit}`;
    }
    if (startTime && startTime > 0) {
      path += `&startTime=${startTime}`;
    }
    if (endTime && endTime > 0) {
      path += `&endTime=${endTime}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as BasisResponse);
  }

  /**
   * Get index info
   * Uses HTTP endpoint /market/v1/indexInfo (matches Go client GetIndexInfo)
   */
  async getIndexInfo(
    symbol: string,
  ): Promise<IndexInfoResponse | IndexInfoResponse[]> {
    let path = "/market/v1/indexInfo";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body;
  }

  /**
   * Get asset index
   * Uses HTTP endpoint /market/v1/assetIndex (matches Go client GetAssetIndex)
   */
  async getAssetIndex(
    symbol: string,
  ): Promise<AssetIndexResponse | AssetIndexResponse[]> {
    let path = "/market/v1/assetIndex";
    if (symbol) {
      path += `?symbol=${symbol}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body;
    return Array.isArray(body) ? body : (body as AssetIndexResponse);
  }

  /**
   * Get constituents
   * Uses HTTP endpoint /market/v1/constituents (matches Go client GetConstituents)
   */
  async getConstituents(symbol: string): Promise<ConstituentsResponse> {
    const response = await this.httpClient.get(
      `/market/v1/constituents?symbol=${symbol}`,
      this.getHeaders(),
    );
    return response.body as ConstituentsResponse;
  }

  /**
   * Get TWAP (time-weighted average price)
   * Uses HTTP endpoint /market/v1/twap (matches Go client GetTWAP)
   */
  async getTWAP(
    symbol: string,
    durationMinutes: number,
  ): Promise<TWAPResponse> {
    const response = await this.httpClient.get(
      `/market/v1/twap?symbol=${symbol}&duration=${durationMinutes}`,
      this.getHeaders(),
    );
    return response.body as TWAPResponse;
  }

  // ============================================================================
  // USER DATA ENDPOINTS (Session Auth Required)
  // ============================================================================

  /**
   * Get transaction history for an address. GET /bank/v1/transactions/history per bank_api.md.
   * Query params: address, txType (transfer|cross_chain|mint|burn), limit, offset.
   */
  async getTransactionHistory(params: {
    address: string;
    txType?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    address: string;
    transactions: Array<{
      txhash: string;
      type: string;
      from: string;
      to: string;
      amount: string;
      collateralAssetIndex: { ticker: string };
      timestamp: { seconds: number; nanos: number };
      status: string;
    }>;
    pagination: {
      limit: number;
      offset: number;
      total: number;
    };
  }> {
    const queryParams = new URLSearchParams({
      address: params.address,
    });
    if (params.txType) queryParams.set("txType", params.txType);
    if (params.limit) queryParams.set("limit", params.limit.toString());
    if (params.offset) queryParams.set("offset", params.offset.toString());

    const response = await this.httpClient.get(
      `/bank/v1/transactions/history?${queryParams.toString()}`,
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get bucket metrics
   * GET /bucket/v1/metrics
   * Important for portfolio analysis
   */
  async getBucketMetrics(): Promise<{
    total_margin: string;
    used_margin: string;
    available_margin: string;
    margin_ratio: string;
    risk_level: string;
    liquidation_price?: string;
  }> {
    const response = await this.httpClient.get(
      "/bucket/v1/metrics",
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get capital efficiency metrics
   * GET /bucket/v1/metrics/efficiency
   * Important for portfolio analysis
   */
  async getBucketEfficiencyMetrics(): Promise<{
    capital_efficiency_ratio: string;
    roi_24h?: string;
    sharpe_ratio?: string;
    sortino_ratio?: string;
    max_drawdown?: string;
  }> {
    const response = await this.httpClient.get(
      "/bucket/v1/metrics/efficiency",
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get address PnL
   * GET /bucket/v1/pnl/address/{address}
   * Important for performance tracking
   */
  async getAddressPnL(address: string): Promise<{
    address: string;
    total_pnl: string;
    unrealized_pnl: string;
    realized_pnl: string;
    positions: Array<{
      market_index: string;
      pnl: string;
    }>;
  }> {
    const response = await this.httpClient.get(
      `/bucket/v1/pnl/address/${encodeURIComponent(address)}`,
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get bucket PnL
   * GET /bucket/v1/pnl/bucket/{bucket_id}
   * Important for performance tracking
   */
  async getBucketPnL(bucketId: string): Promise<{
    bucket_id: string;
    total_pnl: string;
    unrealized_pnl: string;
    realized_pnl: string;
  }> {
    const response = await this.httpClient.get(
      `/bucket/v1/pnl/bucket/${encodeURIComponent(bucketId)}`,
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get positions for authenticated user
   * Uses HTTP endpoint /bucket/v1/address/{address}/positions (matches Go client GetUserPositions)
   * Note: Requires walletAddress to be set or passed
   * 
   * Actual API response: Direct array of RawPosition objects
   * Also handles wrapped format { positions: [...] } for backward compatibility
   */
  async getPositions(
    walletAddress: string,
    activeOnly?: boolean,
  ): Promise<RawPosition[]> {
    if (!walletAddress) {
      throw new Error("walletAddress is required for getPositions");
    }
    let path = `/bucket/v1/address/${walletAddress}/positions`;
    if (activeOnly !== undefined) {
      path += `?active_only=${activeOnly}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as PositionsResponse | RawPosition[];
    // Normalize response to always return an array
    // Handle cases where API returns direct array (actual format) or wrapped { positions: [...] }
    if (Array.isArray(body)) {
      return body;
    }
    if (
      body &&
      typeof body === "object" &&
      "positions" in body &&
      Array.isArray(body.positions)
    ) {
      return body.positions;
    }
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get balance for an address. Uses /bank/v1/balance/{address}?asset= per bank_api.md (query param: asset).
   * Single-balance response may return asset as object (BalanceAsset); SDK normalizes to asset: symbol.
   * @param address - Account address
   * @param asset - Asset symbol (e.g. MORM, USDC). Default on server: MORM.
   */
  async getBalance(address: string, asset: string): Promise<RawBalance> {
    const path = `/bank/v1/balance/${encodeURIComponent(address)}?asset=${encodeURIComponent(asset)}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    const b = response.body as RawBalance & { asset?: string | { symbol?: string } };
    const rawAsset = b.asset as string | { symbol?: string } | undefined;
    if (typeof rawAsset === "object" && rawAsset?.symbol) {
      return { ...b, asset: rawAsset.symbol };
    }
    if (!rawAsset && (b as any).collateralAssetIndex) {
      const t = (b as any).collateralAssetIndex?.ticker ?? (b as any).collateralAssetIndex;
      if (typeof t === "string") return { ...b, asset: t };
    }
    return b;
  }

  /**
   * Get balances for an address
   * Uses HTTP endpoint /bank/v1/balances/{address} (matches Go client GetBalances)
   *
   * Actual API response structure (proto camelCase):
   * {
   *   "address": "0x...",
   *   "balances": [
   *     {
   *       "asset": { "assetIndex": "0", "symbol": "MORM", "type": "ASSET_TYPE_NATIVE_MORM", ... },
   *       "availableBalance": "100000000000000000000000",
   *       "balance": "100000000000000000000000",
   *       "lockedBalance": "0",
   *       "shardId": ""
   *     },
   *     ...
   *   ],
   *   "shardId": ""
   * }
   */
  async getBalances(address: string): Promise<RawBalance[]> {
    const response = await this.httpClient.get(
      `/bank/v1/balances/${address}`,
      this.getHeaders(),
    );
    const body = response.body as GetBalancesResponse;

    // Handle the actual API response structure (GetBalancesResponse)
    if (
      body &&
      typeof body === "object" &&
      "balances" in body &&
      Array.isArray(body.balances)
    ) {
      // Normalize: API returns asset as object (BalanceAsset); set asset to symbol for backward compat
      return body.balances.map((b: RawBalance & { asset?: string | { symbol?: string } }) => {
        const rawAsset = b.asset as string | { symbol?: string } | undefined;
        if (typeof rawAsset === "string") return b;
        if (rawAsset && typeof rawAsset === "object" && typeof rawAsset.symbol === "string") {
          return { ...b, asset: rawAsset.symbol };
        }
        const ticker = (b as any).collateralAssetIndex?.ticker ?? (b as any).collateralAssetIndex;
        if (ticker && typeof ticker === "string") {
          return { ...b, asset: ticker };
        }
        return b;
      });
    }

    return [];
  }

  /**
   * Get balance with decimal conversion. Passes assetID as collateralAssetIndex to getBalance.
   * @param assetID - Collateral asset ticker (e.g. MORM, USDC).
   */
  async getBalanceDecimal(
    address: string,
    assetID: string,
  ): Promise<RawBalance> {
    const balance = await this.getBalance(address, assetID);

    if (balance && typeof balance === "object") {
      const converted: RawBalance & Record<string, any> = { ...balance };
      if (converted.balance) {
        converted.balance = fromSatoshi(converted.balance) as any;
      }
      if (converted.availableBalance) {
        converted.available = fromSatoshi(converted.availableBalance);
      }
      if (converted.available && typeof converted.available === "string") {
        converted.available = fromSatoshi(converted.available);
      }
      if (converted.lockedBalance) {
        converted.locked = fromSatoshi(converted.lockedBalance);
      }
      if (converted.locked && typeof converted.locked === "string") {
        converted.locked = fromSatoshi(converted.locked);
      }
      // Type assertion: converted has all RawBalance properties (spread from balance)
      // even though some values are converted from string to number
      return converted as RawBalance;
    }

    return balance;
  }

  /**
   * Get orders for an address (QueryOrdersByAddress). Proto: GET /clob/v1/address/{address}/orders.
   * @param address - Wallet address
   * @param symbol - Optional market filter (ticker or market id)
   */
  async getOrders(
    address: string,
    symbol?: string,
  ): Promise<OrdersResponse> {
    const opts = symbol ? { marketIndex: symbol } : undefined;
    return this.getOrdersByAddress(address, { ...opts, limit: opts?.limit ?? 500 });
  }

  /**
   * Query orders by market (QueryOrdersByMarket). GET /clob/v1/market/{marketIndex}/orders.
   * Optional: symbol, status, paginationRequest.offset, paginationRequest.limit per clob_api.md.
   */
  async getOrdersByMarket(
    marketIndex: string,
    opts?: {
      symbol?: string;
      status?: string;
      offset?: number;
      limit?: number;
    },
  ): Promise<OrdersResponse> {
    const params = new URLSearchParams();
    if (opts?.symbol) params.set("symbol", opts.symbol);
    if (opts?.status) params.set("status", opts.status);
    if (opts?.offset != null) params.set("paginationRequest.offset", String(opts.offset));
    if (opts?.limit != null) params.set("paginationRequest.limit", String(opts.limit));
    const qs = params.toString();
    const path = `/clob/v1/market/${encodeURIComponent(marketIndex)}/orders${qs ? `?${qs}` : ""}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as OrdersResponse;
    return (
      body ?? {
        orders: [],
        totalCount: 0,
        timestamp: Date.now(),
      }
    );
  }

  /**
   * Query orders by address (QueryOrdersByAddress). Canonical proto path.
   * GET /clob/v1/address/{address}/orders with optional filters.
   */
  async getOrdersByAddress(
    address: string,
    opts?: { marketIndex?: string; status?: string; limit?: number; offset?: number },
  ): Promise<OrdersResponse> {
    const path = `/clob/v1/address/${encodeURIComponent(address)}/orders`;
    const params = new URLSearchParams();
    if (opts?.marketIndex) params.set('marketIndex', opts.marketIndex);
    if (opts?.status) params.set('status', opts.status);
    if (opts?.limit != null) params.set('paginationRequest.limit', String(opts.limit));
    if (opts?.offset != null) params.set('paginationRequest.offset', String(opts.offset));
    const query = params.toString();
    const url = query ? `${path}?${query}` : path;
    const response = await this.httpClient.get(url, this.getHeaders());
    const body = response.body as OrdersResponse;
    return body ?? { orders: [], totalCount: 0, timestamp: Date.now() };
  }

  /**
   * Get order by ID.
   * Uses HTTP endpoint GET /clob/v1/order/{orderID}?address={address}.
   * The address parameter is required for routing the query to the correct morphcore node.
   */
  async getOrder(orderID: string, address: string): Promise<RawOrder> {
    const response = await this.httpClient.get(
      `/clob/v1/order/${encodeURIComponent(orderID)}?address=${encodeURIComponent(address)}`,
      this.getHeaders(),
    );
    return response.body as RawOrder;
  }

  /**
   * Get order history for an address. Alias for getOrdersByAddress (QueryOrdersByAddress).
   * @deprecated Prefer getOrdersByAddress or getOrders for clarity.
   */
  async getOrderHistory(address: string): Promise<OrdersResponse> {
    return this.getOrdersByAddress(address);
  }

  /**
   * Get liquidation history for an address
   * GET /clob/v1/address/{address}/liquidations
   * Important for user analytics
   */
  async getAddressLiquidations(params: {
    address: string;
    limit?: number;
    offset?: number;
    start_time?: number;
    end_time?: number;
  }): Promise<{
    address: string;
    liquidations: Array<{
      liquidation_id: string;
      market_index: string;
      loss: string;
      timestamp: number;
    }>;
    total_count: number;
    limit: number;
    offset: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params.limit != null) queryParams.set("limit", params.limit.toString());
    if (params.offset != null) queryParams.set("offset", params.offset.toString());
    if (params.start_time != null) queryParams.set("start_time", params.start_time.toString());
    if (params.end_time != null) queryParams.set("end_time", params.end_time.toString());

    const response = await this.httpClient.get(
      `/clob/v1/address/${encodeURIComponent(params.address)}/liquidations${queryParams.toString() ? `?${queryParams.toString()}` : ""}`,
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get user trades for an address. Uses /clob/v1/address/trades with symbol per clob_api.
   */
  async getUserTrades(
    address: string,
    symbol?: string,
  ): Promise<RawTrade[]> {
    let path = `/clob/v1/address/trades?address=${encodeURIComponent(address)}`;
    if (symbol) {
      path += `&symbol=${encodeURIComponent(symbol)}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as TradesResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "trades" in body &&
      Array.isArray(body.trades)
    ) {
      return body.trades;
    }

    // Fallback for legacy formats (direct array or { data: [...] })
    if (Array.isArray(body)) {
      return body;
    }
    // Check for legacy format with 'data' property (not part of TradesResponse type)
    const bodyAny = body as any;
    if (bodyAny?.data && Array.isArray(bodyAny.data)) {
      return bodyAny.data;
    }

    return [];
  }

  /**
   * Get trade history (alias for getUserTrades)
   */
  async getTradeHistory(
    address: string,
    symbol?: string,
  ): Promise<RawTrade[]> {
    return this.getUserTrades(address, symbol);
  }

  /**
   * Get position for an address. Uses /clob/v1/address/position with symbol per clob_api.
   */
  async getPosition(
    address: string,
    symbol?: string,
  ): Promise<RawPosition[]> {
    let path = `/clob/v1/address/position?address=${encodeURIComponent(address)}`;
    if (symbol) {
      path += `&symbol=${encodeURIComponent(symbol)}`;
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as PositionsResponse;

    // Handle the actual API response structure
    if (
      body &&
      typeof body === "object" &&
      "positions" in body &&
      Array.isArray(body.positions)
    ) {
      return body.positions;
    }

    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get buckets for a wallet address
   * Uses HTTP endpoint /bucket/v1/address/{address}/buckets (matches Go client GetBuckets)
   * Response format: { success: boolean, message: string, buckets: [...], total_buckets: number }
   * @returns Array of RawBucket objects (raw API format with snake_case fields and satoshi strings)
   */
  async getBuckets(walletAddress: string): Promise<RawBucket[]> {
    const response = await this.httpClient.get(
      `/bucket/v1/address/${walletAddress}/buckets`,
      this.getHeaders(),
    );
    // Normalize response to always return an array
    // Handle cases where API returns:
    // - { success: true, message: "...", buckets: [...], total_buckets: number } (new format)
    // - { buckets: [...] } (old format)
    // - Direct array (legacy format)
    if (response.body?.buckets && Array.isArray(response.body.buckets)) {
      return response.body.buckets as RawBucket[];
    }
    // Return empty array if response structure is unexpected
    return [];
  }

  /**
   * Get bucket by ID
   * Uses HTTP endpoint /bucket/v1/bucket/{bucket_id} (matches Go client GetBucket).
   * API returns QueryBucketResponse { success, message, bucket }; we return the nested bucket.
   */
  async getBucket(bucketId: string): Promise<RawBucket> {
    const response = await this.httpClient.get(
      `/bucket/v1/bucket/${bucketId}`,
      this.getHeaders(),
    );
    const body = response.body as { bucket?: RawBucket };
    if (body?.bucket != null) {
      return body.bucket;
    }
    return response.body as RawBucket;
  }

  /**
   * Get market data. /clob/v1/market/data is deprecated (501); per clob_api use orderbook snapshot
   * and derive lastPrice from best bid/ask. Uses GET /clob/v1/orderbook/{marketIndex}/snapshot.
   */
  async getMarketData(marketIndexOrSymbol: string): Promise<MarketDataResponse> {
    const path = `/clob/v1/orderbook/${encodeURIComponent(marketIndexOrSymbol)}/snapshot?depth=1`;
    const response = await this.httpClient.get(path, this.getHeaders());
    const ob = response.body as { bids?: Array<{ price: string }>; asks?: Array<{ price: string }>; symbol?: string };
    const bids = ob?.bids ?? [];
    const asks = ob?.asks ?? [];
    const bestBid = bids[0]?.price;
    const bestAsk = asks[0]?.price;
    let lastPrice: number | undefined;
    if (bestBid != null && bestAsk != null) {
      lastPrice = (parseFloat(bestBid) + parseFloat(bestAsk)) / 2;
    } else if (bestBid != null) lastPrice = parseFloat(bestBid);
    else if (bestAsk != null) lastPrice = parseFloat(bestAsk);
    return {
      ...(ob ?? {}),
      symbol: ob?.symbol,
      assetPair: ob?.symbol,
      lastPrice: lastPrice != null ? String(lastPrice) : undefined,
      last_price: lastPrice,
      markPrice: lastPrice != null ? String(lastPrice) : undefined,
      mark_price: lastPrice,
    } as MarketDataResponse;
  }

  /**
   * Get market klines (candles). Path style per clob_api: GET /clob/v1/market/{marketIndex}/klines.
   * Action must be present; API uses "klines" not "candles". Query: interval (required), limit.
   */
  async getMarketCandles(
    marketIndexOrSymbol: string,
    interval: string,
    limit: number,
  ): Promise<MarketCandlesResponse> {
    const params = new URLSearchParams({ interval, limit: String(limit) });
    const path = `/clob/v1/market/${encodeURIComponent(marketIndexOrSymbol)}/klines?${params.toString()}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as { klines?: RawKline[] } | RawKline[];
    if (Array.isArray(body)) return body;
    const klines = (body as { klines?: RawKline[] })?.klines;
    return Array.isArray(klines) ? klines : [];
  }

  // ============================================================================
  // STREAM AND STREAMING (Resync and Checksum) — stream_api, streaming_api
  // ============================================================================

  /**
   * Get orderbook snapshot from Router local state for fast resync.
   * GET /v1/stream/orderbook/{market_index}/snapshot?depth=
   */
  async getOrderbookSnapshot(
    marketIndex: string,
    depth: number = 100,
  ): Promise<RawOrderbook & { checksum?: string; source?: string }> {
    const path = `/v1/stream/orderbook/${encodeURIComponent(marketIndex)}/snapshot?depth=${depth}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body as RawOrderbook & { checksum?: string; source?: string };
  }

  /**
   * Get checksum from Router local orderbook state. GET /v1/stream/local-checksum/{market_index}?depth=
   */
  async getLocalChecksum(
    marketIndex: string,
    depth: number = 25,
  ): Promise<{
    market_index: string;
    checksum: string;
    sequence_id?: number;
    depth: number;
    timestamp?: number;
    source?: string;
  }> {
    const path = `/v1/stream/local-checksum/${encodeURIComponent(marketIndex)}?depth=${depth}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body as any;
  }

  /**
   * Get local state statistics. GET /v1/stream/stats
   */
  async getStreamStats(): Promise<{
    markets_tracked?: number;
    total_updates?: number;
    last_update?: number;
    memory_usage_bytes?: number;
    active_subscriptions?: number;
    timestamp?: number;
  }> {
    const response = await this.httpClient.get(
      "/v1/stream/stats",
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get checksum for any stream type. GET /v1/stream/checksum?stream_type=&stream_key=&depth=&address=
   * Response includes is_valid (wire: is_valid) per stream/checksum API.
   */
  async getChecksum(
    streamType: string,
    streamKey: string,
    depth?: number,
    address?: string,
  ): Promise<{
    stream_type?: string;
    stream_key?: string;
    checksum?: string;
    sequence_id?: number;
    item_count?: number;
    is_valid?: boolean;
    timestamp?: unknown;
    metadata?: Record<string, unknown>;
  }> {
    const params = new URLSearchParams({
      stream_type: streamType,
      stream_key: streamKey,
    });
    if (depth != null) params.set("depth", String(depth));
    if (address) params.set("address", address);
    const response = await this.httpClient.get(
      `/v1/stream/checksum?${params.toString()}`,
      this.getHeaders(),
    );
    return response.body as any;
  }

  /**
   * Get orderbook checksum. Path style per clob_api: GET /clob/v1/market/{marketIndex}/checksum.
   */
  async getOrderbookChecksum(
    marketIndex: string,
    depth: number = 25,
  ): Promise<{
    market_index: string;
    checksum: string;
    sequence_id?: number;
    depth?: number;
    timestamp?: unknown;
  }> {
    const path = `/clob/v1/market/${encodeURIComponent(marketIndex)}/checksum?depth=${depth}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as { marketIndex?: number; checksum?: string; sequenceId?: number };
    return {
      market_index: String(body?.marketIndex ?? marketIndex),
      checksum: body?.checksum ?? "",
      sequence_id: body?.sequenceId,
      depth,
      timestamp: (response.body as any)?.timestamp,
    };
  }

  /**
   * If local checksum differs from currentChecksum, fetch and return orderbook snapshot for resync.
   * Returns { mismatch: true, snapshot } when resync needed, { mismatch: false } otherwise.
   */
  async resyncOrderbookIfMismatch(
    marketIndex: string,
    currentChecksum: string | null,
    depth: number = 25,
  ): Promise<{ mismatch: boolean; snapshot?: RawOrderbook }> {
    const local = await this.getLocalChecksum(marketIndex, depth);
    if (currentChecksum != null && local.checksum !== currentChecksum) {
      const snapshot = await this.getOrderbookSnapshot(marketIndex, depth);
      return { mismatch: true, snapshot };
    }
    return { mismatch: false };
  }

  /**
   * Get nonce status for an address. Uses /router/v1/nonce/status/{address} per nonce_api.
   * @param reserve - If true, atomically reserves the returned nonce.
   */
  async getNonceStatus(
    address: string,
    reserve: boolean = false,
  ): Promise<NonceStatusResponse> {
    let path = `/router/v1/nonce/status/${encodeURIComponent(address)}`;
    if (reserve) {
      path += "?reserve=true";
    }
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body as NonceStatusResponse;
  }

  /**
   * Atomically reserve a batch of consecutive nonces for a wallet.
   * Uses GET /router/v1/nonce/batch/{address}?count=N.
   *
   * This is far more efficient than N individual getNonceStatus(reserve=true) calls
   * because it makes a single HTTP round-trip and holds the server-side per-wallet
   * mutex for the entire batch, guaranteeing consecutive, conflict-free nonces.
   *
   * @param address - Wallet address
   * @param count - Number of nonces to reserve (1–1000)
   * @returns Array of reserved nonces in ascending order
   */
  async getNonceBatch(
    address: string,
    count: number,
  ): Promise<number[]> {
    const path = `/router/v1/nonce/batch/${encodeURIComponent(address)}?count=${count}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    const body = response.body as { nonces?: number[]; count?: number };
    return body?.nonces ?? [];
  }

  /**
   * Get next nonce from Auth (simple, no reserve). Uses GET /auth/v1/nonce?owner= per auth_api.
   * For reserve/lifecycle use getNonceStatus (router) instead.
   */
  async getAuthNonce(owner: string): Promise<{ owner?: string; next_nonce?: number; timestamp?: unknown }> {
    const path = `/auth/v1/nonce?owner=${encodeURIComponent(owner)}`;
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body as any;
  }

  /**
   * Roll back a nonce. POST /router/v1/nonce/rollback. **Test mode only** per nonce_api.
   */
  async nonceRollback(address: string, nonce: string): Promise<{ success?: boolean; message?: string; [key: string]: any }> {
    const response = await this.httpClient.post(
      "/router/v1/nonce/rollback",
      { address, nonce },
      this.getHeaders(),
    );
    return response.body as any;
  }

  // ============================================================================
  // ROUTER V1 ENDPOINTS (Unified Write Service & Health)
  // ============================================================================

  /**
   * Get supported action types
   * Uses HTTP endpoint /router/v1/tx/supported-actions
   * @returns Array of supported action type strings
   */
  async getSupportedActions(): Promise<SupportedActionsResponse> {
    const response = await this.httpClient.get(
      "/router/v1/tx/supported-actions",
      this.getHeaders(),
    );
    return response.body as SupportedActionsResponse;
  }

  /**
   * Validate action before submission
   * Uses HTTP endpoint /router/v1/tx/validate-action
   * @param action - Action type to validate (e.g., "clob::submit_order")
   * @returns Validation result with valid flag
   */
  async validateAction(action: string): Promise<ValidateActionResponse> {
    const response = await this.httpClient.get(
      `/router/v1/tx/validate-action?action=${encodeURIComponent(action)}`,
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get aggregated system health overview
   * Uses HTTP endpoint /router/v1/health/summary
   * @returns System health summary with overall status and component health
   */
  async getHealthSummary(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/summary",
      this.getHeaders(),
    );
    return response.body as HealthSummaryResponse;
  }

  /**
   * Get detailed daemon status (relayer/mailbox)
   * Uses HTTP endpoint /router/v1/health/daemons
   * @returns Detailed daemon status information
   */
  async getHealthDaemons(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/daemons",
      this.getHeaders(),
    );
    return response.body;
  }

  /**
   * Get risk engine health status. GET /router/v1/health/risk per health_api.
   */
  async getHealthRisk(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/risk",
      this.getHeaders(),
    );
    return response.body as HealthSummaryResponse;
  }

  /**
   * Get bucket health status. GET /router/v1/health/bucket.
   * @deprecated Prefer getHealthRisk() for risk engine; retain for bucket-specific checks.
   */
  async getHealthBucket(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/bucket",
      this.getHeaders(),
    );
    return response.body as HealthSummaryResponse;
  }

  /**
   * Circuit breaker status. GET /router/v1/health/circuit-breaker.
   */
  async getHealthCircuitBreaker(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/circuit-breaker",
      this.getHeaders(),
    );
    return response.body as HealthSummaryResponse;
  }

  /**
   * CoreDaemon status. GET /router/v1/health/coredaemon.
   */
  async getHealthCoredaemon(): Promise<HealthSummaryResponse> {
    const response = await this.httpClient.get(
      "/router/v1/health/coredaemon",
      this.getHeaders(),
    );
    return response.body as HealthSummaryResponse;
  }

  /**
   * Liveness probe. GET /health/live.
   */
  async getHealthLive(): Promise<{ status?: string; [key: string]: any }> {
    const response = await this.httpClient.get("/health/live", this.getHeaders());
    return response.body as any;
  }

  /**
   * Readiness probe. GET /health/ready. Query: verbose, strict.
   */
  async getHealthReady(verbose?: boolean, strict?: boolean): Promise<{ status?: string; [key: string]: any }> {
    const params = new URLSearchParams();
    if (verbose) params.set("verbose", "true");
    if (strict) params.set("strict", "true");
    const q = params.toString();
    const path = q ? `/health/ready?${q}` : "/health/ready";
    const response = await this.httpClient.get(path, this.getHeaders());
    return response.body as any;
  }

  /**
   * Get server time in milliseconds (Binance-compatible)
   * Uses HTTP endpoint /router/v1/time
   * @returns Server time in milliseconds (Unix timestamp)
   */
  async getServerTime(): Promise<ServerTimeResponse> {
    const response = await this.httpClient.get(
      "/router/v1/time",
      this.getHeaders(),
    );
    return response.body as ServerTimeResponse;
  }

  /**
   * Get transactions per second metrics
   * Uses HTTP endpoint /router/v1/metrics/tps
   * @returns TPS statistics including current, peak, and averages
   */
  async getMetricsTPS(): Promise<TPSMetricsResponse> {
    const response = await this.httpClient.get(
      "/router/v1/metrics/tps",
      this.getHeaders(),
    );
    return response.body as TPSMetricsResponse;
  }

  /**
   * Get latency statistics (P50, P95, P99, Avg, Max, Min)
   * Uses HTTP endpoint /router/v1/metrics/latency
   * @returns Latency percentiles and statistics
   */
  async getMetricsLatency(): Promise<LatencyMetricsResponse> {
    const response = await this.httpClient.get(
      "/router/v1/metrics/latency",
      this.getHeaders(),
    );
    return response.body as LatencyMetricsResponse;
  }
}
